import useReveal from '../hooks/useReveal';
import SectionHead from './SectionHead';

/**
 * Problem — observations, not scare statistics.
 *
 * The previous version led with "7% per second of delay", "75% judge you
 * by your design", and "89% won't come back" — borrowed clickbait metrics
 * we can't defend when asked where the numbers come from. Replacing with
 * three honest observations framed from the perspective of a small-business
 * owner evaluating their current site. No numbers, because this section
 * is about naming the problem, not measuring it.
 */
interface Observation {
  label: string;
  title: string;
  body: string;
}

const OBSERVATIONS: Observation[] = [
  {
    label: 'Speed',
    title: 'A site that feels slow makes the business feel slow.',
    body: "The first thing a visitor judges is how fast the page responds. If tapping a link feels sticky, most people close the tab before they've read a word of your copy.",
  },
  {
    label: 'Credibility',
    title: 'Outdated design signals outdated service.',
    body: "A shop that cooks beautifully or fixes cars honestly doesn't deserve a site that looks like 2009. Fair or not, visitors use the website as a proxy for the work.",
  },
  {
    label: 'Mobile',
    title: 'Most of your visitors are on a phone — one-handed.',
    body: "If the menu is a pinch-to-zoom, the phone number isn't tappable, and the reservation button sits below three carousels, the site is costing you bookings you'll never see.",
  },
];

export default function Problem() {
  return (
    <section className="section problem">
      <div className="wrap">
        <SectionHead
          eyebrow="Why this matters"
          title={
            <>
              Your site isn’t just old. <em className="serif">It’s getting in the way.</em>
            </>
          }
          body="A business website doesn't need to be clever. It needs to be fast, clear, and current. When it isn't, visitors don't tell you — they quietly leave and try a competitor."
        />

        <ul className="prob-grid">
          {OBSERVATIONS.map((o, i) => (
            <ObservationCard key={o.label} card={o} index={i} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function ObservationCard({ card, index }: { card: Observation; index: number }) {
  const ref = useReveal<HTMLLIElement>();
  const delay = index > 0 ? `d${Math.min(index, 4)}` : '';
  return (
    <li ref={ref} className={`prob-card reveal ${delay}`}>
      <p className="prob-label">{card.label}</p>
      <h3>{card.title}</h3>
      <p>{card.body}</p>
    </li>
  );
}
